import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Star } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useCompany } from "../context/CompanyContext";

/**
 * /nps — public landing target for the NPS quick-vote buttons embedded
 * in the post-cleaning email. URL: /nps?bid=<id>&t=<token>&score=<0-10>
 *
 * On mount: validates the token via /api/public/nps/lookup, auto-submits
 * the score if present in the URL, and lets the user adjust the rating
 * + leave an optional comment afterwards.
 */
export default function Nps() {
  const { company } = useCompany();
  const [params] = useSearchParams();
  const bid = params.get("bid") || "";
  const token = params.get("t") || "";
  const initialScore = parseInt(params.get("score") || "");

  const [stage, setStage] = useState("loading");
  const [booking, setBooking] = useState(null);
  const [score, setScore] = useState(Number.isFinite(initialScore) ? initialScore : null);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!bid || !token) {
        setStage("invalid");
        return;
      }
      try {
        const { data } = await api.get(`/public/nps/lookup?bid=${bid}&t=${token}`);
        if (cancelled) return;
        setBooking(data.booking);
        // Auto-submit if a score came in the URL
        if (Number.isFinite(initialScore) && initialScore >= 0 && initialScore <= 10) {
          await submit(initialScore, "");
        } else {
          setStage("rate");
        }
      } catch (err) {
        if (!cancelled) {
          setError(formatApiError(err));
          setStage("invalid");
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(s, c) {
    try {
      await api.post("/public/nps/submit", {
        booking_id: bid, token, score: s, comment: c || null,
      });
      setScore(s);
      setStage("done");
    } catch (err) {
      setError(formatApiError(err));
      setStage("invalid");
    }
  }

  return (
    <div className="min-h-screen bg-[#031c18] text-[#fdfbf7] flex flex-col">
      <header className="px-6 lg:px-12 py-6 border-b border-[#1a4640] flex items-center justify-between">
        <Link to="/" className="text-[#a0b2af] hover:text-[#d4af37] text-sm">← Página inicial</Link>
        <div className="font-display text-xl tracking-wide text-[#d4af37]">{company.trade_name}</div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto px-6 lg:px-12 py-16 w-full" data-testid="nps-page">
        {stage === "loading" && (
          <div className="text-center text-[#a0b2af]">A validar o link…</div>
        )}

        {stage === "invalid" && (
          <div className="lux-card p-10 text-center" data-testid="nps-invalid">
            <AlertCircle size={36} className="text-[#ef4444] mx-auto mb-4" />
            <h1 className="font-display text-3xl mb-2">Link inválido ou expirado</h1>
            <p className="text-sm text-[#a0b2af] mb-6">
              {error || "O link clicado já não é válido. Pode partilhar a sua opinião na página pública de testemunhos."}
            </p>
            <Link to="/testemunhos" className="btn-gold px-6 py-3 text-sm inline-flex items-center gap-2">
              Deixar testemunho <Star size={14} />
            </Link>
          </div>
        )}

        {stage === "rate" && (
          <div className="space-y-8" data-testid="nps-rate">
            <div className="space-y-3">
              <div className="eyebrow text-[#d4af37]">Avaliação rápida · 30 segundos</div>
              <h1 className="font-display text-4xl">Como foi a limpeza?</h1>
              {booking && (
                <p className="text-sm text-[#a0b2af]">
                  Limpeza {booking.apt_type}
                  {booking.apartment_name && <> · <strong className="text-[#d4af37]">{booking.apartment_name}</strong></>}
                  {booking.scheduled_date && <> · {booking.scheduled_date}</>}
                </p>
              )}
            </div>

            <div className="lux-card p-6 space-y-5">
              <p className="text-sm">De 0 a 10, qual a probabilidade de nos recomendar a outro host?</p>
              <div className="grid grid-cols-11 gap-1.5">
                {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                  <button key={n} onClick={() => setScore(n)}
                          className={`py-3 text-sm font-semibold rounded-sm border transition-all ${
                            score === n
                              ? "bg-[#d4af37] text-[#031c18] border-[#d4af37]"
                              : n <= 6
                              ? "border-[#ef4444]/40 text-[#fdfbf7] hover:border-[#ef4444]"
                              : n <= 8
                              ? "border-[#f59e0b]/40 text-[#fdfbf7] hover:border-[#f59e0b]"
                              : "border-[#10b981]/40 text-[#fdfbf7] hover:border-[#10b981]"
                          }`}
                          data-testid={`nps-score-${n}`}>
                    {n}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-[#a0b2af] uppercase tracking-widest">
                <span>Pouco provável</span><span>Muito provável</span>
              </div>

              <div className="space-y-2 pt-3 border-t border-[#1a4640]">
                <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">
                  Quer deixar um comentário? (opcional)
                </label>
                <textarea
                  className="lux-input w-full min-h-[80px]"
                  placeholder="O que mais gostou… ou o que podemos melhorar."
                  value={comment}
                  onChange={e => setComment(e.target.value.slice(0, 500))}
                  data-testid="nps-comment"
                />
              </div>

              <button onClick={() => score !== null && submit(score, comment)}
                      disabled={score === null}
                      className="btn-gold w-full py-3 text-sm disabled:opacity-40"
                      data-testid="nps-submit">
                Enviar avaliação
              </button>
            </div>
          </div>
        )}

        {stage === "done" && (
          <div className="lux-card p-10 text-center" data-testid="nps-done">
            <CheckCircle2 size={48} className="text-[#10b981] mx-auto mb-5" />
            <h1 className="font-display text-3xl mb-3">
              {score >= 9 ? "Que ótima notícia!" :
               score >= 7 ? "Obrigado pelo feedback!" :
               "Lamentamos não ter superado expectativas."}
            </h1>
            <p className="text-sm text-[#a0b2af] mb-2">
              Avaliação registada: <strong className="text-[#d4af37] text-lg">{score}/10</strong>
            </p>
            {score >= 9 && (
              <>
                <p className="text-sm text-[#a0b2af] mb-6 max-w-md mx-auto">
                  Quer transformar este feedback num testemunho público? Bastam mais 30 segundos.
                </p>
                <Link to="/testemunhos" className="btn-gold px-6 py-3 text-sm inline-flex items-center gap-2"
                      data-testid="nps-cta-testimonial">
                  Deixar testemunho <Star size={14} />
                </Link>
              </>
            )}
            {score < 9 && (
              <p className="text-sm text-[#a0b2af] mt-6">
                A sua opinião vai diretamente para o admin. Vamos contactá-lo se precisarmos de mais detalhe.
              </p>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-[#1a4640] px-6 lg:px-12 py-6 text-center text-xs text-[#a0b2af]">
        {company.legal_name} · Limpezas profissionais &amp; lavandaria
      </footer>
    </div>
  );
}
